import { useMemo } from 'react'
import { useGetAdvisorInsightsDataQuery, useGetInsightsDataQuery } from '../../common/insightsDataApi'
import { getDateRange } from '../../shared/insights/utils/insightsTransforms'
import { INSIGHTS_CACHE_TTL_SECONDS } from '../../shared/insights/utils/insightsUtils'
import { type UserScoreRow } from '../../shared/insights/store/insights'
import { RANGE_TO_TIME_RANGE, RANGE_TO_USER_SCORE_LIST_CHART, type RangeKey } from './userScoreRange'

interface UseUserScoresDataOptions {
  enabled?: boolean
  userIds?: string[]
}

const useUserScoresDataArgs = (accountId: string, range: RangeKey, enabled: boolean, userIds?: string[]) => {
  const { from, to } = getDateRange(RANGE_TO_TIME_RANGE[range])
  return {
    args: { accountId, from, to, userIdList: userIds, charts: [RANGE_TO_USER_SCORE_LIST_CHART[range]] },
    options: { skip: !enabled || !accountId, refetchOnMountOrArgChange: INSIGHTS_CACHE_TTL_SECONDS },
  }
}

const useScorecards = (data: ReturnType<typeof useGetAdvisorInsightsDataQuery>['data']) =>
  useMemo<UserScoreRow[]>(() => data?.charts?.user_score_list ?? [], [data])

export const useUserScoresData = (accountId: string, range: RangeKey, { enabled = true, userIds }: UseUserScoresDataOptions = {}) => {
  const { args, options } = useUserScoresDataArgs(accountId, range, enabled, userIds)
  const { data, isFetching, isLoading, isError } = useGetAdvisorInsightsDataQuery(args, options)
  const scorecards = useScorecards(data)

  return { scorecards, isFetching, isLoading, isError }
}

export const useUserScoresGroupedData = (accountId: string, range: RangeKey, { enabled = true, userIds }: UseUserScoresDataOptions = {}) => {
  const { args, options } = useUserScoresDataArgs(accountId, range, enabled, userIds)
  const { data, isFetching, isLoading, isError } = useGetInsightsDataQuery(args, options)
  const scorecards = useScorecards(data)

  return { scorecards, isFetching, isLoading, isError }
}
